/**
 * @file        src/pages/blog/AddBlogCategory.jsx
 * @description Create / edit a blog category (name, slug, image upload, tags,
 *              SEO meta). Reused for /blog/categories/new and .../:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-09-15 Claude — Language is a locked English-only dropdown reading
 *                          "English", not a free-text box defaulting to "en".
 *                          It accepted any 2-8 character string for a storefront
 *                          that serves English only, so a row saved as hi/mr was
 *                          content nobody could read. Options come from
 *                          lib/languages.js — one list for all five forms.
 *   - 2026-07-01 Claude — Initial (Blog Management).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../lib/languages';

const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(160),
  slug: z.string().trim().max(180).optional(),
  tags: z.string().trim().max(500).optional(),
  meta_title: z.string().trim().max(255).optional(),
  meta_keyword: z.string().trim().max(2000).optional(),
  meta_description: z.string().trim().max(2000).optional(),
  language: z.enum(['en'], { errorMap: () => ({ message: 'English only' }) }),
  is_active: z.boolean(),
});

const empty = {
  name: '', slug: '', image: '', tags: '', meta_title: '',
  meta_keyword: '', meta_description: '', language: DEFAULT_LANGUAGE, is_active: true,
};

export default function AddBlogCategory() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.blogCategories.one(id),
    queryFn: () => get(`/blog-categories/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const c = existing.data;
      setForm({
        name: c.name || '', slug: c.slug || '', image: c.image || '', tags: c.tags || '',
        meta_title: c.meta_title || '', meta_keyword: c.meta_keyword || '',
        meta_description: c.meta_description || '', language: c.language || DEFAULT_LANGUAGE, is_active: !!c.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/blog-categories/${id}`, b) : post('/blog-categories', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Category updated' : 'Category added', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
      navigate('/blog/categories');
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    const body = { ...parsed.data };
    for (const k of ['slug', 'tags', 'meta_title', 'meta_keyword', 'meta_description']) if (!body[k]) delete body[k];
    if (form.image) body.image = form.image;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Blog Category' : 'Add Blog Category'}
        subtitle="Blog Management"
        actions={<button className="btn ghost" onClick={() => navigate('/blog/categories')}><Icon name="x" /> Cancel</button>}
      />
      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Category Name" required err={errors.name}>
            <input value={form.name} onChange={on('name')} placeholder="e.g. Domestic" />
          </Field>
          <Field label="Category Slug" err={errors.slug} hint="Auto from name if blank.">
            <input value={form.slug} onChange={on('slug')} placeholder="e.g. domestic" />
          </Field>
          <Field label="Category Tags" err={errors.tags}>
            <input value={form.tags} onChange={on('tags')} placeholder="comma,separated,tags" />
          </Field>

          <Field label="Category Image" full>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          </Field>

          <Field label="Language" required err={errors.language}
                 hint="English is the only language the website serves.">
            <Select
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              options={LANGUAGE_OPTIONS}
            />
          </Field>
          <Field label="Meta Title" err={errors.meta_title}>
            <input value={form.meta_title} onChange={on('meta_title')} placeholder="SEO title" />
          </Field>
          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
          </Field>

          <Field label="Meta Keyword" err={errors.meta_keyword} full>
            <textarea rows={2} value={form.meta_keyword} onChange={on('meta_keyword')} placeholder="keyword1, keyword2" />
          </Field>
          <Field label="Meta Description" err={errors.meta_description} full>
            <textarea rows={3} value={form.meta_description} onChange={on('meta_description')} placeholder="SEO description" />
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
